import { useState, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import {
  ActiveOrgProvider,
  useActiveOrganization,
} from './hooks/useActiveOrganization';
import { DeepLinkProvider, useDeepLink } from './hooks/useDeepLink';
import { ModalProvider } from './hooks/useModalManager';
import { ThemeProvider } from './styles/theme';
import { SizingProvider } from './styles/sizing';
import { PlayfulProvider } from './styles/playful';
import { IconProvider } from './icons';
import { ToastProvider, ToastContainer } from './components/core/Toast';
import { queryClient } from './lib/query-client';
import LoginForm from './screens/Login';
import Dashboard from './screens/Dashboard';
import ComingSoon from './components/ComingSoon';
import { FadeIn, FloatingShapes } from './components/AnimatedElements';
import {
  ThemeMascot,
  LoginMascotShowcase,
} from './components/core/ThemeMascot';
import { LogoTransitionOverlay } from './components/animations/LogoTransitionOverlay';
import {
  useLoginTransitionStore,
  resetTransition,
} from './stores/loginTransitionStore';
import {
  parseDeepLink,
  storePendingDeepLink,
  retrievePendingDeepLink,
  cleanDeepLinkUrl,
} from './utils/deep-linking';
import { validateAllThemesInDev } from './styles/colors/themes/dev-validation';
import { logger } from '@/utils/logger';

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { setPendingDeepLink } = useDeepLink();
  const [showComingSoon, setShowComingSoon] = useState(false);
  const transitionStore = useLoginTransitionStore();

  // Parse deep links on mount
  useEffect(() => {
    const deepLink = parseDeepLink();

    if (deepLink) {
      logger.debug('[App] Deep link detected:', { deepLink });

      if (isAuthenticated) {
        // User is authenticated - store to localStorage so a refresh before
        // Dashboard processes the link still works, then clean the URL.
        storePendingDeepLink(deepLink);
        setPendingDeepLink(deepLink);
        cleanDeepLinkUrl();
      } else {
        // User is not authenticated - store for post-login navigation
        storePendingDeepLink(deepLink);
        // Clean URL now to show clean login page
        cleanDeepLinkUrl();
        logger.debug('[App] Stored deep link for post-login navigation');
      }
    } else if (isAuthenticated && !isLoading) {
      // Check for pending deep link after login
      const pendingLink = retrievePendingDeepLink();
      if (pendingLink) {
        logger.debug('[App] Restoring pending deep link after login:', {
          pendingLink,
        });
        setPendingDeepLink(pendingLink);
      }
    }
  }, [isAuthenticated, isLoading, setPendingDeepLink]);

  // Show loading state (only if NOT in the middle of the login transition)
  if (isLoading && !transitionStore.visible) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLoginSuccess = () => {
    // Auth context handles state — overlay will animate to header
  };

  const handleLoginFailure = () => {
    setShowComingSoon(true);
  };

  return (
    <>
      {/* Render the active view */}
      {isAuthenticated ? (
        <>
          <Dashboard />
          <ThemeMascot scale={1} />
          <ThemeMascot scale={0.7} />
          <ThemeMascot scale={1.25} />
          <ThemeMascot scale={0.85} />
        </>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-purple-100 flex items-center justify-center p-4 relative overflow-hidden">
          <FloatingShapes />
          <LoginMascotShowcase />
          <FadeIn direction="up" delay={200} duration={0.8}>
            <LoginForm
              onLoginSuccess={handleLoginSuccess}
              onLoginFailure={handleLoginFailure}
            />
          </FadeIn>
          <ComingSoon open={showComingSoon} onOpenChange={setShowComingSoon} />
        </div>
      )}

      <ToastContainer />
    </>
  );
}

/**
 * Login transition overlay — rendered OUTSIDE OrgScopedContent
 * so it is never remounted by the org key change on login.
 */
function LoginTransitionLayer() {
  const store = useLoginTransitionStore();
  if (!store.visible) return null;
  return <LogoTransitionOverlay onFinish={() => resetTransition()} />;
}

/**
 * Wraps AppContent with a React key based on active org ID.
 * When org switches, the entire tree unmounts and remounts,
 * destroying all local state and forcing all queries to refetch.
 */
function OrgScopedContent() {
  const { activeOrganizationId } = useActiveOrganization();
  return <AppContent key={activeOrganizationId} />;
}

function App() {
  // Validate themes in development mode
  useEffect(() => {
    validateAllThemesInDev();
  }, []);

  /**
   * Provider hierarchy order is important:
   * 1. QueryClientProvider - React Query (needed by ThemeProvider & SizingProvider)
   * 2. ThemeProvider - Provides theme colors and mode (uses preferences query)
   * 3. SizingProvider - Provides sizing multiplier and tokens (uses preferences query)
   * 3.5. PlayfulProvider - Provides playful interaction themes (uses preferences query)
   * 4. IconProvider - Uses theme colors and sizing (depends on 2 & 3)
   * 5. ToastProvider - Uses theme and sizing (depends on 2, 3 & 4)
   * 6. AuthProvider - Uses icons, theme, and toast (depends on 2, 3, 4 & 5)
   * 7. ModalProvider - Manages modal stack and navigation queue (depends on 6)
   * 8. DeepLinkProvider - Coordinates deep link navigation (depends on 6 & 7)
   * 9. ActiveOrgProvider - Uses auth context (depends on 6)
   */
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SizingProvider>
          <PlayfulProvider>
            <IconProvider>
              <ToastProvider>
                <AuthProvider>
                  <ModalProvider>
                    <DeepLinkProvider>
                      <ActiveOrgProvider>
                        <OrgScopedContent />
                        <LoginTransitionLayer />
                      </ActiveOrgProvider>
                    </DeepLinkProvider>
                  </ModalProvider>
                </AuthProvider>
              </ToastProvider>
            </IconProvider>
          </PlayfulProvider>
        </SizingProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
